import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../../ui/breadcrumb';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../../ui/table';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { AlertCircle, Info, Lock, HelpCircle, MessageSquareMore, RotateCcw, Home, Pencil } from 'lucide-react';
import Markdown from 'react-markdown';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { getEpicAssessmentSummary } from '../../../api/assessments';
import { getProjectEvaluations, triggerCapexEvaluation, triggerRndEvaluation, updateProjectOverride, updateJustification, pollEvaluationJobUntilDone } from '../../../api/project-evaluations';
import type { EpicWithCalculations, ProjectEvaluation } from '../../../api/shared/types';
import { ApiError } from '../../../api/shared/apiError';
import { mapSummaryToEpicWithCalculations } from '../../../lib/epic-summary-mapping';
import { calculateWeightedAverages } from '../../../lib/review-calculations';
// n8n/Teams approval flow disabled per Change Doc v1.1 (April 2026). Retained for future reference.
// import { submitForApproval } from '../../../lib/approval-submission';
import { approveProjectInApp, reopenProject } from '../../../api/approvals';
import { checkBatchAdminAccess } from '../../../api/batch-admin';
import { EpicDetailModal } from '../epic';
import { ApproveConfirmationModal, ReopenProjectDialog } from '../approval';
import { CalculationBreakdownModal } from '../calculation';
import { FeedbackModal, HelpModal } from '../help';
import { useDivisions, useSoftwareCompanies, useProducts, useApprovalDetails } from '../../../hooks';
// n8n/Teams approval flow used these – retained for future reference.
// import { buildDeepLinkUrl, getInitialWorkspaceId, getInitialInstanceId, getEvoPlatformBaseUrl } from '../../../hooks';
import { useProductSettings } from '@workspace/utils-react';
import { formatDateTimeUK, formatDateUK } from '../../../lib/date-utils';
import { useDevRole } from '../../../lib/devRoleOverride';
import type { Settings } from '../../../AppContainer';

export function ReviewPage() {
  const { projectName, fiscalYearQuarter } = useParams<{
    projectName: string;
    fiscalYearQuarter: string;
  }>();
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { apiUrl } = useProductSettings<Settings>();
  
  // Fetch organization data for breadcrumbs
  const { data: divisions = [] } = useDivisions();
  const { data: softwareCompanies = [] } = useSoftwareCompanies();
  const { data: products = [] } = useProducts({ limit: 1000 });
  
  // Compute breadcrumb data
  const currentProduct = useMemo(() => {
    if (!projectName) return null;
    return products.find(p => p.project_name === projectName) || null;
  }, [products, projectName]);
  
  const currentSoftwareCompany = useMemo(() => {
    if (!currentProduct?.software_company) return null;
    return softwareCompanies.find(c => c.name === currentProduct.software_company) || null;
  }, [softwareCompanies, currentProduct]);
  
  const currentDivision = useMemo(() => {
    if (!currentProduct?.division) return null;
    return divisions.find(d => d.name === currentProduct.division) || null;
  }, [divisions, currentProduct]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [epics, setEpics] = useState<EpicWithCalculations[]>([]);
  const [weightedCapEx, setWeightedCapEx] = useState(0);
  const [weightedRnD, setWeightedRnD] = useState(0);
  const [selectedEpic, setSelectedEpic] = useState<EpicWithCalculations | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasAssessments, setHasAssessments] = useState(false);
  const [projectEvaluation, setProjectEvaluation] = useState<ProjectEvaluation | null>(null);

  const selectedEpicRef = useRef<EpicWithCalculations | null>(null);
  selectedEpicRef.current = selectedEpic;

  // Approval state
  const [showApproveModal, setShowApproveModal] = useState(false);

  // Calculation breakdown modals
  const [showCapExBreakdown, setShowCapExBreakdown] = useState(false);
  const [showRnDBreakdown, setShowRnDBreakdown] = useState(false);

  // Help panel state
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // In-app approval state
  const [isApproving, setIsApproving] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isBatchAdmin, setIsBatchAdmin] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  // Project evaluation trigger state (per-type so each button shows its own spinner)
  const [isEvaluatingCapex, setIsEvaluatingCapex] = useState(false);
  const [isEvaluatingRnd, setIsEvaluatingRnd] = useState(false);
  const isEvaluating = isEvaluatingCapex || isEvaluatingRnd;
  const evaluationAbortRef = useRef<AbortController | null>(null);

  // Justification expand/collapse states
  const [capexExpanded, setCapexExpanded] = useState(false);
  const [rndExpanded, setRndExpanded] = useState(false);

  // Justification inline editing states
  const [isEditingCapexJustification, setIsEditingCapexJustification] = useState(false);
  const [isEditingRndJustification, setIsEditingRndJustification] = useState(false);
  const [editedCapexJustification, setEditedCapexJustification] = useState('');
  const [editedRndJustification, setEditedRndJustification] = useState('');
  const [isSavingJustification, setIsSavingJustification] = useState(false);

  // Fetch approval details when project override is available
  const { data: approvalDetails } = useApprovalDetails(projectEvaluation?.override?.id?.toString() ?? '');

  // Status flags - derived from project evaluation override status
  const statusLower = projectEvaluation?.override?.status?.toLowerCase() ?? '';
  const isDraft = statusLower === 'draft' || statusLower === '' || statusLower === 'active' || (!projectEvaluation?.override && !!projectEvaluation);
  const isInProgress = statusLower === 'inprogress';
  const isApproved = statusLower === 'approved';
  const isReopened = statusLower === 'reopened' || statusLower === 'withdrawn';
  // Per Change Doc v1.1: only Approved locks the project. Submitted/Rejected are legacy states.
  const isLocked = isApproved;

  // Re-open button is visible when project is approved AND current user is the approver OR is batch admin
  const canReopen = isApproved
    && (isBatchAdmin || (
      !!approvalDetails?.approver_email
      && !!currentUserEmail
      && approvalDetails.approver_email.toLowerCase() === currentUserEmail.toLowerCase()
    ));

  const canApprove = (isDraft || isInProgress || isReopened) && (epics.length > 0 || !!projectEvaluation) && hasAssessments && !isApproving;

  const getApproveTooltip = () => {
    if (!isDraft && !isInProgress && !isReopened) return 'Already approved';
    if (epics.length === 0 && !projectEvaluation) return 'No EPICs or project evaluation available';
    if (!hasAssessments) return 'At least one EPIC must be assessed';
    return '';
  };

  // Helper function to truncate text
  const truncateText = (text: string | null | undefined, maxLength = 150) => {
    if (!text) return null;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength);
  };

  useEffect(() => {
    loadData();
  }, [projectName, fiscalYearQuarter]);

  useEffect(() => {
    return () => {
      evaluationAbortRef.current?.abort();
    };
  }, []);

  const devRole = useDevRole();
  // The dev role switcher only works locally; on deployed environments the effective
  // role comes from the backend (check-access). Prefer the dev override when present.
  const effectiveRole = devRole ?? currentUserRole;
  const isReadOnlyRole = effectiveRole === 'ReadOnly' || effectiveRole === 'NoAccess';
  // Only approvers/administrators may approve. Gate as a positive allow-list so the
  // button stays hidden until the role is known (never flashes for read-only users).
  const canApproveByRole = effectiveRole === 'Approver' || effectiveRole === 'Administrator';

  // Fetch batch admin access and current user email (re-runs on dev role change)
  useEffect(() => {
    async function fetchAdminAccess() {
      try {
        const result = await checkBatchAdminAccess(apiUrl);
        setIsBatchAdmin(result.has_access);
        setCurrentUserEmail(result.email ?? '');
        setCurrentUserRole(result.permission ?? null);
      } catch {
        setIsBatchAdmin(false);
        setCurrentUserRole(null);
      }
    }
    fetchAdminAccess();
  }, [apiUrl, devRole]);

  // Re-fetch project data when dev role changes
  useEffect(() => {
    if (devRole !== undefined) {
      loadData();
    }
  }, [devRole]);

  async function loadData(silent = false) {
    if (!projectName || !fiscalYearQuarter) {
      if (!silent) {
        setError('Missing required parameters');
        setLoading(false);
      }
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      // Fetch epic assessment summaries and project evaluations in parallel
      // (they are independent) to cut page load time roughly in half.
      const [summaries, evaluations] = await Promise.all([
        getEpicAssessmentSummary(apiUrl, { projectName: projectName, fiscalYearQuarter: fiscalYearQuarter }),
        getProjectEvaluations(apiUrl, fiscalYearQuarter).catch((evalErr) => {
          console.warn('ProjectApproval_ Failed to fetch project evaluations:', evalErr);
          return [] as ProjectEvaluation[];
        }),
      ]);

      if (!Array.isArray(summaries)) {
        throw new Error('Failed to fetch epic assessment summaries - invalid data format');
      }

      const epicsWithCalculations = summaries.map(mapSummaryToEpicWithCalculations);

      setEpics(epicsWithCalculations);

      const currentSelectedEpic = selectedEpicRef.current;
      if (currentSelectedEpic) {
        const updatedEpic = epicsWithCalculations.find(e => e.id === currentSelectedEpic.id);
        if (updatedEpic) {
          setSelectedEpic(updatedEpic);
        }
      }

      const { weightedCapEx: capEx, weightedRnD: rnd } = calculateWeightedAverages(epicsWithCalculations);
      setWeightedCapEx(capEx);
      setWeightedRnD(rnd);

      const hasAssessedEpics = summaries.some(s => s.assessed_at);

      // Use already-fetched evaluations
      const matchingEval = evaluations.find(
        (e) => e.project_name === projectName
      );
      setHasAssessments(hasAssessedEpics || !!matchingEval);
      setProjectEvaluation(matchingEval ?? null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load review data';
      if (!silent) {
        setError(errorMessage);
        toast.error(`Failed to load review: ${errorMessage}`);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }



  // n8n/Teams approval flow used deriveNameFromEmail – retained for future reference.
  // const deriveNameFromEmail = (email: string): string => {
  //   const localPart = email.split('@')[0];
  //   return localPart
  //     .split('.')
  //     .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  //     .join(' ');
  // };

  // n8n/Teams approval flow disabled per Change Doc v1.1 (April 2026). Retained for future reference.
  // const handleSubmitForApproval = async (
  //   approverEmail: string,
  //   approverName: string,
  //   comments: string
  // ) => {
  //   if (!projectEvaluation) return;
  //   setIsSubmitting(true);
  //   try {
  //     const effectiveProjectName = projectName ?? '';
  //     const effectiveFiscalYearQuarter = fiscalYearQuarter ?? '';
  //     const effectiveProjectRoot = projectRoot ?? '';
  //     const effectiveDisplayName = currentProduct?.project_name ?? projectEvaluation?.area_level_1 ?? effectiveProjectName;
  //     const submittedByEmail = approvalDetails?.submitted_by || 'user@example.com';
  //     const submittedByName = approvalDetails?.submitted_by_name || deriveNameFromEmail(submittedByEmail);
  //     const xApiKey = import.meta.env.VITE_X_API_KEY || '';
  //     const reviewRoute = `/review/${encodeURIComponent(effectiveProjectName)}/${encodeURIComponent(effectiveFiscalYearQuarter)}/${encodeURIComponent(effectiveProjectRoot)}`;
  //     const evoPlatformBase = getEvoPlatformBaseUrl(workspaceUrl);
  //     const capturedWorkspaceId = getInitialWorkspaceId();
  //     const capturedInstanceId = getInitialInstanceId();
  //     const templateId = 'project-approval.native-tab';
  //     const evoUrl = new URL(evoPlatformBase);
  //     if (capturedWorkspaceId) { evoUrl.searchParams.set('workspaceId', capturedWorkspaceId); }
  //     evoUrl.searchParams.set('templateId', templateId);
  //     if (capturedInstanceId) { evoUrl.searchParams.set('instanceId', capturedInstanceId); }
  //     const deeplink = buildDeepLinkUrl(evoUrl.toString(), {
  //       route: reviewRoute, projectName: effectiveProjectName,
  //       fiscalYearQuarter: effectiveFiscalYearQuarter, projectRoot: effectiveProjectRoot,
  //     });
  //     const effectiveAreaLevel1 = projectEvaluation?.area_level_1 ?? effectiveDisplayName;
  //     let approvalToken: string | undefined;
  //     try {
  //       const overrideResult = await updateProjectOverride(apiUrl, effectiveAreaLevel1, effectiveFiscalYearQuarter, {
  //         status: 'Submitted for Approval',
  //         manual_capex_percentage: Math.round(weightedCapEx),
  //         manual_rnd_percentage: Math.round(weightedRnD),
  //         selected_value: 'Manual',
  //         approver_email: approverEmail, approver_name: approverName, comments,
  //       });
  //       approvalToken = overrideResult.approval_token ?? undefined;
  //     } catch (overrideErr) {
  //       console.warn('ProjectApproval_ Failed to update project override on submit:', overrideErr);
  //     }
  //     if (isN8NEnabled) {
  //       await submitForApproval({ approverEmail, approverName, comments,
  //         finalCapexPercentage: weightedCapEx, finalRndPercentage: weightedRnD,
  //         epicCount: epics.length, projectName: effectiveDisplayName,
  //         fiscalYearQuarter: effectiveFiscalYearQuarter, projectRoot: effectiveProjectRoot,
  //         submittedByEmail, submittedByName, n8nUrl, apiUrl, xApiKey, deeplink,
  //         areaLevel1: effectiveAreaLevel1, approvalToken,
  //       });
  //     }
  //     await loadData();
  //     window.dispatchEvent(new Event('approvals-changed'));
  //     setShowApproveModal(false);
  //     toast.success(`Successfully submitted for approval to ${approverEmail}`, { duration: 5000 });
  //   } catch (err) {
  //     console.error('ProjectApproval_ Error submitting for approval:', err);
  //     toast.error('Failed to submit for approval. Please try again.', { duration: 5000 });
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const handleEpicClick = (epic: EpicWithCalculations) => {
    if (isLocked) {
      toast.info('This Project is locked and cannot be edited');
      return;
    }
    setSelectedEpic(epic);
    setModalOpen(true);
  };

  const handleTriggerProjectEvaluation = async (evalType: 'capex' | 'rnd') => {
    if (!fiscalYearQuarter) return;

    const evalAreaLevel1 = projectEvaluation?.area_level_1 ?? projectName;
    const label = evalType === 'capex' ? 'CapEx' : 'R&D';

    evaluationAbortRef.current?.abort();
    const abortController = new AbortController();
    evaluationAbortRef.current = abortController;

    const setEvaluating = evalType === 'capex' ? setIsEvaluatingCapex : setIsEvaluatingRnd;
    setEvaluating(true);
    try {
      const accepted = evalType === 'capex'
        ? await triggerCapexEvaluation(apiUrl, fiscalYearQuarter, evalAreaLevel1)
        : await triggerRndEvaluation(apiUrl, fiscalYearQuarter, evalAreaLevel1);

      toast.info(`${label} justification evaluation started. This may take a moment...`, { duration: 5000 });

      const result = await pollEvaluationJobUntilDone(apiUrl, accepted.job_id, abortController.signal);

      if (result.status === 'completed') {
        toast.success(`${label} justification re-evaluated successfully`, { duration: 5000 });
        await loadData(true);
      } else {
        const errorDetail = result.error_message ?? 'Unknown error';
        toast.error(`${label} justification evaluation failed: ${errorDetail}`, { duration: 8000 });
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error(`ProjectApproval_ Error during ${evalType} project evaluation:`, err);
      const message = err instanceof Error ? err.message : 'Please try again.';
      toast.error(`${label} project evaluation failed: ${message}`, { duration: 8000 });
    } finally {
      setEvaluating(false);
    }
  };

  const handleEditJustification = (type: 'capex' | 'rnd') => {
    if (type === 'capex') {
      setEditedCapexJustification(projectEvaluation?.capex_justification ?? '');
      setIsEditingCapexJustification(true);
    } else {
      setEditedRndJustification(projectEvaluation?.rnd_justification ?? '');
      setIsEditingRndJustification(true);
    }
  };

  const handleSaveJustification = async (type: 'capex' | 'rnd') => {
    const areaLevel1 = projectEvaluation?.area_level_1 ?? projectName;
    if (!fiscalYearQuarter || !areaLevel1) return;

    setIsSavingJustification(true);
    try {
      const justification = type === 'capex' ? editedCapexJustification : editedRndJustification;
      await updateJustification(apiUrl, areaLevel1, fiscalYearQuarter, type, justification);
      toast.success(`${type === 'capex' ? 'CapEx' : 'R&D'} justification saved successfully`);
      if (type === 'capex') setIsEditingCapexJustification(false);
      else setIsEditingRndJustification(false);
      await loadData(true);
    } catch (_err) {
      toast.error(`Failed to save ${type === 'capex' ? 'CapEx' : 'R&D'} justification`);
    } finally {
      setIsSavingJustification(false);
    }
  };

  const handleCancelEditJustification = (type: 'capex' | 'rnd') => {
    if (type === 'capex') setIsEditingCapexJustification(false);
    else setIsEditingRndJustification(false);
  };

  const handleApproveInApp = async () => {
    if (!projectEvaluation) return;

    setIsApproving(true);
    try {
      const effectiveAreaLevel1 = projectEvaluation.area_level_1 ?? projectName ?? '';
      const effectiveFiscalYearQuarter = fiscalYearQuarter ?? '';

      // Sync current weighted averages to ProjectOverride so the backend
      // snapshots up-to-date values when it copies ManualCapexPercentage
      // into FinalCapexPercentage during approval.
      await updateProjectOverride(apiUrl, effectiveAreaLevel1, effectiveFiscalYearQuarter, {
        status: 'active',
        manual_capex_percentage: Math.round(weightedCapEx),
        manual_rnd_percentage: Math.round(weightedRnD),
        selected_value: 'Manual',
      });

      await approveProjectInApp(apiUrl, effectiveAreaLevel1, effectiveFiscalYearQuarter);
      // Brief delay to ensure the database transaction is fully committed before refreshing
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData();
      await queryClient.invalidateQueries({ queryKey: ['approvalDetails'] });
      window.dispatchEvent(new Event('approvals-changed'));
      setShowApproveModal(false);
      toast.success('Project approved successfully', { duration: 5000 });
    } catch (err) {
      console.error('ProjectApproval_ Error approving project:', err);
      const reason = err instanceof ApiError
        ? `${err.status}: ${err.errorDetail ?? err.errorTitle}`
        : err instanceof Error
          ? err.message
          : 'Unknown error';
      toast.error(`Failed to approve project: ${reason}`, { duration: 8000 });
    } finally {
      setIsApproving(false);
    }
  };

  const handleReopenProject = async () => {
    if (!projectEvaluation) return;

    setIsReopening(true);
    try {
      const effectiveAreaLevel1 = projectEvaluation.area_level_1 ?? projectName ?? '';
      const effectiveFiscalYearQuarter = fiscalYearQuarter ?? '';

      await reopenProject(apiUrl, effectiveAreaLevel1, effectiveFiscalYearQuarter);
      // Brief delay to ensure the database transaction is fully committed before refreshing
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadData();
      await queryClient.invalidateQueries({ queryKey: ['approvalDetails'] });
      window.dispatchEvent(new Event('approvals-changed'));
      setShowReopenDialog(false);
      toast.success('Project reopened successfully', { duration: 5000 });
    } catch (err) {
      console.error('ProjectApproval_ Error reopening project:', err);
      toast.error('Failed to reopen project. Please try again.', { duration: 5000 });
    } finally {
      setIsReopening(false);
    }
  };



  const handleModalClose = () => {
    // Close the modal without reloading data.
    // Data reload is handled by onUpdate (only called after Save).
    setModalOpen(false);
    setSelectedEpic(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!projectEvaluation && epics.length === 0) {
    return (
      <div className="p-6 space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No project evaluation data found</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
          <Home className="h-4 w-4" />
          Home
        </Button>
      </div>
    );
  }

  return (
    <div className="p-12 space-y-6">
      {/* Breadcrumb Navigation and Help Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFeedbackModal(true)}
            className="gap-2"
          >
            <MessageSquareMore className="h-4 w-4" />
            Feedback
          </Button>
          <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate('/')} className="cursor-pointer">
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            {currentDivision && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink 
                    onClick={() => navigate(`/?division=${currentDivision.id}`)} 
                    className="cursor-pointer"
                  >
                    {currentDivision.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {currentSoftwareCompany && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink 
                    onClick={() => navigate(`/?division=${currentDivision?.id}&SoftCo=${currentSoftwareCompany.id}`)} 
                    className="cursor-pointer"
                  >
                    {currentSoftwareCompany.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink 
                onClick={() => navigate(`/?division=${currentDivision?.id}&SoftCo=${currentSoftwareCompany?.id}&Product=${projectName}`)} 
                className="cursor-pointer"
              >
                {currentProduct?.project_name || projectEvaluation?.area_level_1 || projectName}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {fiscalYearQuarter}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
          </Breadcrumb>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHelpPanel(true)}
          className="gap-2"
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </Button>
      </div>

      {/* Locked Banner — only shown when approved */}
      {isLocked && (
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
          <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {approvalDetails?.approver_name && approvalDetails?.approved_at
              ? `This Project has been approved by ${approvalDetails.approver_name} on ${formatDateTimeUK(approvalDetails.approved_at)}.`
              : 'This Project has been approved and is locked.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Re-opened Banner — only shown when re-opened.
          NOTE: The API fields withdrawn_by / withdrawn_at are intentionally reused as re-open
          audit columns (no schema change). If the API contract is ever updated to rename these
          fields, update the references below accordingly. */}
      {isReopened && (
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
          <RotateCcw className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {approvalDetails?.withdrawn_by && approvalDetails?.withdrawn_at
              ? `This Project was re-opened by ${approvalDetails.withdrawn_by} on ${formatDateTimeUK(approvalDetails.withdrawn_at)}.`
              : 'This Project has been re-opened for editing.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Hero Card */}
      <Card>
        <CardHeader>
          {/* Title Row with CapEx % and R&D % */}
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl font-bold">
                {currentProduct?.project_name || projectEvaluation?.area_level_1 || projectName}
              </CardTitle>
              <CardDescription className="text-base mt-1">
                {fiscalYearQuarter}
              </CardDescription>
            </div>
            <div className="flex items-center gap-6 not-prose">
              <div>
                <p className="text-sm text-muted-foreground">CapEx %</p>
                <p className="text-2xl font-bold">{(weightedCapEx ?? 0).toFixed(0)}%</p>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setShowCapExBreakdown(true)}
                  className="h-auto p-0 text-xs"
                >
                  View calculation
                </Button>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">R&D %</p>
                <p className="text-2xl font-bold">{(weightedRnD ?? 0).toFixed(0)}%</p>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setShowRnDBreakdown(true)}
                  className="h-auto p-0 text-xs"
                >
                  View calculation
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Project Evaluation and Justification Sections */}
          <div className="space-y-3">
            {/* CapEx Justification */}
            <div className="border rounded-lg p-3 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">CapEx Justification</span>
                  {projectEvaluation?.capex_justification && (
                    <Badge variant="secondary" className="text-xs">Project Evaluation</Badge>
                  )}
                </div>
                {!isReadOnlyRole && !isLocked && projectEvaluation && (
                  <div className="flex items-center gap-2">
                    {!isEditingCapexJustification && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditJustification('capex')}
                        className="gap-2"
                      >
                        <Pencil className="size-4" />
                        Edit
                      </Button>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isEvaluating || isEditingCapexJustification}
                            onClick={() => handleTriggerProjectEvaluation('capex')}
                            className="gap-2"
                          >
                            {isEvaluatingCapex && <Spinner className="size-4" />}
                            {isEvaluatingCapex ? 'Evaluating...' : 'Re-evaluate CapEx justification'}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>To be used if significant changes to the Project % have been made</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
              <div className="text-sm">
                {isEditingCapexJustification ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedCapexJustification}
                      onChange={(e) => setEditedCapexJustification(e.target.value)}
                      className="w-full min-h-[200px] p-2 border rounded-md text-sm font-mono"
                      placeholder="Enter CapEx justification (Markdown supported)..."
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveJustification('capex')} disabled={isSavingJustification}>
                        {isSavingJustification ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancelEditJustification('capex')} disabled={isSavingJustification}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : projectEvaluation?.capex_justification ? (
                  <div>
                    {capexExpanded ? (
                      <>
                        <div className="prose prose-sm max-w-none dark:prose-invert break-words overflow-hidden">
                          <Markdown>{projectEvaluation.capex_justification}</Markdown>
                        </div>
                        <button 
                          type="button"
                          onClick={() => setCapexExpanded(false)} 
                          className="text-blue-600 hover:underline mt-2 block"
                        >
                          See less
                        </button>
                      </>
                    ) : (
                      <>
                        <span>
                          {truncateText(projectEvaluation.capex_justification, 150)}
                        </span>
                        {projectEvaluation.capex_justification.length > 150 && (
                          <button 
                            type="button"
                            onClick={() => setCapexExpanded(true)} 
                            className="text-blue-600 hover:underline ml-1"
                          >
                            ... See more
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No justification provided yet. Justifications are generated automatically when EPICs are assessed.</p>
                )}
              </div>
            </div>

            {/* R&D Justification */}
            <div className="border rounded-lg p-3 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">R&D Justification</span>
                  {projectEvaluation?.rnd_justification && (
                    <Badge variant="secondary" className="text-xs">Project Evaluation</Badge>
                  )}
                </div>
                {!isReadOnlyRole && !isLocked && projectEvaluation && (
                  <div className="flex items-center gap-2">
                    {!isEditingRndJustification && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditJustification('rnd')}
                        className="gap-2"
                      >
                        <Pencil className="size-4" />
                        Edit
                      </Button>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isEvaluating || isEditingRndJustification}
                            onClick={() => handleTriggerProjectEvaluation('rnd')}
                            className="gap-2"
                          >
                            {isEvaluatingRnd && <Spinner className="size-4" />}
                            {isEvaluatingRnd ? 'Evaluating...' : 'Re-evaluate R&D justification'}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>To be used if significant changes to the Project % have been made</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
              <div className="text-sm">
                {isEditingRndJustification ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedRndJustification}
                      onChange={(e) => setEditedRndJustification(e.target.value)}
                      className="w-full min-h-[200px] p-2 border rounded-md text-sm font-mono"
                      placeholder="Enter R&D justification (Markdown supported)..."
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveJustification('rnd')} disabled={isSavingJustification}>
                        {isSavingJustification ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleCancelEditJustification('rnd')} disabled={isSavingJustification}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : projectEvaluation?.rnd_justification ? (
                  <div>
                    {rndExpanded ? (
                      <>
                        <div className="prose prose-sm max-w-none dark:prose-invert break-words overflow-hidden">
                          <Markdown>{projectEvaluation.rnd_justification}</Markdown>
                        </div>
                        <button 
                          type="button"
                          onClick={() => setRndExpanded(false)} 
                          className="text-blue-600 hover:underline mt-2 block"
                        >
                          See less
                        </button>
                      </>
                    ) : (
                      <>
                        <span>
                          {truncateText(projectEvaluation.rnd_justification, 150)}
                        </span>
                        {projectEvaluation.rnd_justification.length > 150 && (
                          <button 
                            type="button"
                            onClick={() => setRndExpanded(true)} 
                            className="text-blue-600 hover:underline ml-1"
                          >
                            ... See more
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No justification provided yet. Justifications are generated automatically when EPICs are assessed.</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            {isApproved && approvalDetails ? (
              <div className="flex items-center gap-4 flex-wrap">
                {approvalDetails.approver_name && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Approved by</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm font-semibold cursor-default">{approvalDetails.approver_name}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{approvalDetails.approver_email}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
                {approvalDetails.approved_at && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">Approved on</span>
                    <span className="text-sm font-semibold">
                      {formatDateUK(approvalDetails.approved_at)}
                    </span>
                  </div>
                )}
                {/* != null guards against both null and undefined (API can return null, not undefined) */}
                {approvalDetails.final_capex_percentage != null && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">CapEx % at approval</span>
                    <span className="text-sm font-semibold">{approvalDetails.final_capex_percentage.toFixed(0)}%</span>
                  </div>
                )}
                {/* != null guards against both null and undefined (API can return null, not undefined) */}
                {approvalDetails.final_rnd_percentage != null && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">R&D % at approval</span>
                    <span className="text-sm font-semibold">{approvalDetails.final_rnd_percentage.toFixed(0)}%</span>
                  </div>
                )}
              </div>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              {canApproveByRole && (isDraft || isInProgress || isReopened) && !isApproved && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Button
                          onClick={() => setShowApproveModal(true)}
                          disabled={!canApprove}
                        >
                          Approve
                        </Button>
                      </div>
                    </TooltipTrigger>
                    {!canApprove && (
                      <TooltipContent>
                        <p>{getApproveTooltip()}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              {isApproved && canReopen && (
                <Button
                  variant="outline"
                  onClick={() => setShowReopenDialog(true)}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-950 gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Re-open Project
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* EPICs Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <div className="flex items-baseline gap-3">
            <CardTitle>EPICs</CardTitle>
            <span className="text-sm italic text-muted-foreground">
              Click on an EPIC to view details
            </span>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total EPICs</p>
            <p className="text-2xl font-bold">{epics.length}</p>
          </div>
        </CardHeader>
        <CardContent>
          {epics.length === 0 ? (
            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                No parent Epics in this quarter. The CapEx and R&D assessment shown is
                based on project-level analysis of work items that don&apos;t roll up to an
                Epic. Contact the PMO if further information is required.
              </AlertDescription>
            </Alert>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>EPIC ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="text-right">CapEx %</TableHead>
                  <TableHead className="text-right">R&D %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {epics.map((epic) => {
                  const isFlat = epic.id.startsWith('FLAT_');
                  return (
                    <TableRow
                      key={epic.id}
                      className={isLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
                      onClick={() => !isLocked && handleEpicClick(epic)}
                    >
                      <TableCell>
                        {isFlat ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline">All closed items</Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>This project has no Epics. All closed work items have been grouped and assessed together.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Badge variant="outline">{epic.id}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {epic.title}
                        {isLocked && <Lock className="inline-block ml-2 h-3 w-3 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="text-right">{(epic.activeCapExPercentage ?? 0).toFixed(0)}%</TableCell>
                      <TableCell className="text-right">{(epic.activeRnDPercentage ?? 0).toFixed(0)}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-muted/50 font-semibold">
                  <TableCell colSpan={2} className="text-right">
                    {epics.length === 0 && "n/a — see project-level assessment above"}
                  </TableCell>
                  <TableCell className="text-right">
                    {epics.length === 0 ? "n/a" : `${(weightedCapEx ?? 0).toFixed(0)}%`}
                  </TableCell>
                  <TableCell className="text-right">
                    {epics.length === 0 ? "n/a" : `${(weightedRnD ?? 0).toFixed(0)}%`}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* EPIC Detail Modal */}
      {selectedEpic && (
        <EpicDetailModal
          epic={selectedEpic}
          reviewId={projectEvaluation?.override?.id?.toString() ?? ''}
          open={modalOpen}
          onOpenChange={handleModalClose}
          onUpdate={() => loadData(true)}
        />
      )}

      {/* Approve Confirmation Modal */}
      {projectEvaluation && (
        <ApproveConfirmationModal
          open={showApproveModal}
          onOpenChange={setShowApproveModal}
          onApprove={handleApproveInApp}
          projectName={currentProduct?.project_name || projectEvaluation?.area_level_1 || projectName || ''}
          fiscalYearQuarter={fiscalYearQuarter || ''}
          epicCount={epics.length}
          weightedCapEx={weightedCapEx}
          weightedRnD={weightedRnD}
        />
      )}

      {/* Reopen Project Dialog */}
      <ReopenProjectDialog
        open={showReopenDialog}
        onOpenChange={setShowReopenDialog}
        onConfirm={handleReopenProject}
        isReopening={isReopening}
        projectName={currentProduct?.project_name || projectEvaluation?.area_level_1 || projectName || ''}
      />



      {/* Help Modal */}
      <HelpModal
        open={showHelpPanel}
        onOpenChange={setShowHelpPanel}
      />

      {/* Feedback Modal */}
      <FeedbackModal open={showFeedbackModal} onOpenChange={setShowFeedbackModal} />

      {/* CapEx Calculation Breakdown Modal */}
      <CalculationBreakdownModal
        open={showCapExBreakdown}
        onOpenChange={setShowCapExBreakdown}
        type="capex"
        epics={epics}
        reviewId={projectEvaluation?.override?.id?.toString()}
        onEpicClick={(epic) => {
          handleEpicClick(epic);
        }}
        onTotalChange={(total) => {
          setWeightedCapEx(total);
        }}
        onUpdate={() => {
          loadData(true);
        }}
        fiscalYearQuarter={fiscalYearQuarter}
        areaLevel1={projectEvaluation?.area_level_1}
      />

      {/* R&D Calculation Breakdown Modal */}
      <CalculationBreakdownModal
        open={showRnDBreakdown}
        onOpenChange={setShowRnDBreakdown}
        type="rnd"
        epics={epics}
        reviewId={projectEvaluation?.override?.id?.toString()}
        onEpicClick={(epic) => {
          handleEpicClick(epic);
        }}
        onTotalChange={(total) => {
          setWeightedRnD(total);
        }}
        onUpdate={() => {
          loadData(true);
        }}
        fiscalYearQuarter={fiscalYearQuarter}
        areaLevel1={projectEvaluation?.area_level_1}
      />
    </div>
  );
}
